/** Scheduling: pages/schedules.tsx + all of components/scheduling/*. */
export const schedules = {
  // ── shared domain atoms (reused across sections) ──────────────────────────
  allDevices: "All devices",
  percent: "{value}%",
  optional: "(optional)",
  entryN: "Slot {n}",
  pickPlaylist: "Pick a playlist…",
  noChannelPlaylists: "No {channel} playlists yet",
  fromLabel: "From",
  toLabel: "To",
  audiencesN: { one: "{count} audience", other: "{count} audiences" },
  entriesCount: { one: "{count} slot", other: "{count} slots" },

  intentLabel: {
    audio_only: "Audio only",
    video_only: "Video only",
    video_muted: "Video, muted",
    av_full: "Audio + video",
  },
  kindLabel: {
    play: "Play",
    exception_play: "Exception",
    off: "Off",
  },
  behaviorLabel: {
    wait: "Wait",
    interrupt: "Interrupt",
  },
  field: {
    channel: "Channel",
    bus: "Output",
    where: "Where",
    entries: "Slots",
    review: "Review",
    volume: "Volume",
  },

  // ── audience-clause-fields.tsx ────────────────────────────────────────────
  clauseFields: {
    help: "Include rules add matching devices; exclude rules remove them. With no rules the audience matches all devices.",
    mode: { include: "Include", exclude: "Exclude" },
    selector: {
      location: "Location",
      device: "Device",
      label: "Label",
    },
    selectLocation: "Select location",
    selectDevice: "Select device",
    labelKeyPlaceholder: "Label key (e.g. region)",
    labelValuePlaceholder: "Value (e.g. north)",
    labelHint:
      "Labels are set on devices and locations. Select devices in Sites and use the Label action to tag them.",
    labelHintLink: "Open Sites",
    removeRule: "Remove rule",
    noRules: "No rules yet. This audience reaches all devices.",
    addRule: "Add rule",
  },

  // ── bus-step.tsx ──────────────────────────────────────────────────────────
  busStep: {
    heading: "Sound, picture, or both?",
    subhead: "Pick how this content reaches the screen and speakers.",
    desc: {
      audio_only: "Sound only, no picture",
      av_full: "Full picture and sound",
      video_muted: "Show the video, drop its own audio",
      video_only: "Picture only, no audio from this",
    },
    volumeHelp: "The schedule's volume. Each slot can override it.",
  },

  // ── channel-step.tsx ──────────────────────────────────────────────────────
  channelStep: {
    heading: "Which channel?",
    subhead: "Every schedule plays on one channel.",
    desc: {
      music: "Background music for the floor",
      ads: "Promotional spots between content",
      announcements: "Store notices & messages",
      video: "Screen content & signage",
    },
  },

  // ── entries-step.tsx ──────────────────────────────────────────────────────
  entriesStep: {
    help: "The plays, exceptions and off-times that make up this schedule. Higher priority wins when they overlap.",
    addEntry: "Add slot",
    empty: "No slots yet. Add one to say what this schedule plays.",
    priorityLabel: "Priority {value}",
    copySuffix: "{label} (copy)",
    moveUp: "Move up",
    moveDown: "Move down",
    duplicate: "Duplicate",
  },

  // ── entry-content-field.tsx ───────────────────────────────────────────────
  entryContent: {
    tab: {
      playlists: "Playlists",
      files: "Files",
      library: "Library",
      signage: "Web / Widgets",
    },
    sourceTag: {
      Upload: "Upload",
      Library: "Library",
      Playlist: "Playlist",
    },
    toast: {
      noChannel: "No {channel} channel to hold the content",
      poolCreateFailed: "Couldn't create the schedule's content set",
      uploadFailed: "Upload failed: {name}",
      addTrackFailed: "Couldn't add track",
      addFileFailed: "Couldn't add file",
      importFailed: "Couldn't import {name}",
    },
    poolName: "{label} set",
    poolLabelFallback: "Schedule",
    trackFallback: "Track",
    itemsInSchedule: {
      one: "{count} item in this schedule",
      other: "{count} items in this schedule",
    },
    buildingSet: "Building a set for this schedule",
    perSchedulePool: "Per-schedule set",
    sub: {
      playlist: "Playlist · referenced (no copy)",
      singleFile: "Single file · referenced",
      webPage: "Web page · fullscreen",
      widget: "Widget · fullscreen",
    },
    change: "Change",
    searchPlaylists: "Search playlists…",
    noPlaylistsMatch: "No playlists match your search.",
    noPlaylistsYet: "No playlists yet.",
    addTracks: "Add tracks",
    playlistsHelp:
      "Click a playlist to play it directly, or \"Add tracks\" to fold it into a per-schedule set.",
    searchFiles: "Search your uploaded files…",
    noUploadedFiles: "No uploaded {channel} files yet. Add some on the Upload tab.",
    added: "Added",
    filesHelp:
      "Click a file to play it directly, or \"Add\" to include it in a per-schedule set.",
    working: "Working…",
    dropHint: "Drag files here, or browse",
    noSignage: "No web pages or widgets yet. Create them in the Screen library.",
    webPage: "Web page",
    widgetKind: "Widget · {kind}",
    signageHelp: "Shows fullscreen on the screen for the whole scheduled window.",
    inThisSchedule: "In this schedule",
    rotation: "Rotation",
    rotationOption: {
      even: "Even",
      weighted: "Weighted",
      sequential: "Sequential",
    },
    weightAria: "weight",
  },

  // ── entry-editor.tsx ──────────────────────────────────────────────────────
  entryEditor: {
    section: {
      type: "Type",
      content: "Content",
      when: "When",
      behaviour: "Behaviour",
    },
    kindDesc: {
      play: "Play content on this schedule",
      exception_play: "Override what's playing for special days",
      off: "Timed silence. Nothing plays.",
    },
    behaviorDesc: {
      wait: "Let the current item finish, then start",
      interrupt: "Start immediately, cutting off the current item",
    },
    whatThisDoes: "What this slot does",
    labelOptional: "Label (optional)",
    labelPlaceholderContent: "e.g. Morning playlist",
    labelPlaceholderOff: "e.g. Closed for cleaning",
    howToPlay: "How to play",
    rotation: { sequential: "Sequential", shuffle: "Shuffle" },
    imageDurationTitle: "Image display duration (optional)",
    imageDurationPlaceholder: "10",
    imageDurationHelp:
      "Seconds each still image stays on screen. Empty = the image's own duration, or 10s.",
    volumeOverrideTitle: "Volume override (optional)",
    usesScheduleVolume: "Uses the schedule's volume.",
    startBehaviour: "Start behaviour",
    priority: "Priority",
    customValue: "Custom value",
  },

  // ── entry-model.ts ────────────────────────────────────────────────────────
  entryModel: {
    priorityTier: {
      takeover: { label: "Takeover", desc: "Cuts in over everything below it" },
      operational: { label: "Operational", desc: "Safety / operational messages" },
      promotion: { label: "Promotion", desc: "Ads and promotions" },
      filler: { label: "Filler", desc: "Background / base content" },
    },
    summary: {
      silence: "Silence",
      singleFile: "Single file",
      noFile: "No file",
      webPageWidget: "Web page / widget",
      noContent: "No content",
    },
    windowN: { one: "{count} window", other: "{count} windows" },
    timesN: "{count} times",
    everyMin: "every {n}m",
    everyItems: "every {n} items",
    hourly: "hourly",
    always: "Always",
    orSeparator: " OR ",
  },

  // ── multi-audience-picker.tsx ─────────────────────────────────────────────
  audiencePicker: {
    allDevicesDesc: "Everything in this workspace",
    noAudiences: "No audiences yet. Create one to target specific locations or devices.",
    reachesAll: "Reaches all devices.",
    summary: "{audiences} · ≈{devices}, {locations}",
    newAudience: "New audience",
  },

  // ── multi-date-range.tsx ──────────────────────────────────────────────────
  dateRange: {
    always: "Always published. Add a window to limit the dates.",
    removeWindow: "Remove window",
    addWindow: "Add window",
  },

  // ── preset-days-picker.tsx ────────────────────────────────────────────────
  holidayPicker: {
    searchPlaceholder: "Search holidays…",
    countryPlaceholder: "Country",
    workspaceLocations: "Workspace locations: {list}",
    loadError: "Could not load calendar presets.",
    noDates: "No dates match your country, packs, or search.",
    nextSeparator: " · Next: ",
    movable: "Movable",
  },

  // ── quick-schedule-dialog.tsx ─────────────────────────────────────────────
  quickDialog: {
    toast: {
      created: "Schedule created and activated",
      createFailed: "Couldn't create the schedule",
    },
    title: "Quick schedule",
    intro:
      "Put a playlist on the air in one step. It goes live immediately, and you can fine-tune it later from the planner.",
    playlist: "Playlist",
    when: "When",
    whoHearsIt: "Who hears it",
    willBeCreatedAs: "Will be created as",
    creating: "Creating…",
    createGoLive: "Create & go live",
  },

  // ── recurrence-builder.tsx ────────────────────────────────────────────────
  recurrence: {
    weekday: {
      sun: "S",
      mon: "M",
      tue: "T",
      wed: "W",
      thu: "T",
      fri: "F",
      sat: "S",
    },
    month: {
      jan: "Jan",
      feb: "Feb",
      mar: "Mar",
      apr: "Apr",
      may: "May",
      jun: "Jun",
      jul: "Jul",
      aug: "Aug",
      sep: "Sep",
      oct: "Oct",
      nov: "Nov",
      dec: "Dec",
    },
    nth: { n1: "1st", n2: "2nd", n3: "3rd", n4: "4th", n5: "5th" },
    nthGeneric: "{n}th",
    last: "Last",
    periodNoun: {
      daily: "day",
      weekly: "week",
      monthly: "month",
      yearly: "year",
    },
    periodFallback: "period",
    everyNPeriods: "every {n} {noun}s",
    stopsAfter: "stops after {count}",
    untilDate: "until {date}",
    posSummary: "pos {list}",
    extraCount: "+{count} extra",
    skippedCount: "−{count} skipped",
    freq: {
      none: "Every day (no pattern)",
      daily: "Daily",
      weekly: "Weekly",
      monthly: "Monthly",
      yearly: "Yearly",
    },
    weekdaysPreset: "Weekdays",
    onDays: "On day(s)",
    onWeekdays: "On weekday(s)",
    inTheseMonths: "In these months",
    advanced: "Advanced",
    repeatEvery: "Repeat every",
    onlyNthMatch: "Only the Nth match",
    bySetPosHint: "(optional)",
    ends: "Ends",
    never: "Never",
    afterNTimes: "After N times",
    onADate: "On a date",
    occurrencesAria: "Occurrences",
    untilDateAria: "Until date",
    onlyInMonths: "Only in these months",
    alsoInclude: "Also include these dates",
    skipDates: "Skip these dates",
    removeDate: "Remove {date}",
  },

  // ── review-step.tsx ───────────────────────────────────────────────────────
  review: {
    quickCheck: "Quick check, then save.",
    dash: "—",
    noEntries: "No slots yet.",
    priorityShort: "P{value}",
    volSuffix: " · {value}% vol",
  },

  // ── schedule-timeline.tsx ─────────────────────────────────────────────────
  timeline: {
    channelLabel: {
      music: "Music",
      ads: "Ads",
      announcements: "Notices",
      video: "Video",
    },
    flightFrom: "from {date}",
    flightUntil: "until {date}",
    flightOngoing: "ongoing",
    noEntries: "no slots",
    today: "Today",
    earlier: "Earlier",
    later: "Later",
    tooltip: {
      where: "Where: {value}",
      what: "What: {value}",
      when: "When: {value}",
      dragDay: "(drag to shift the daily time · double-click to edit)",
      dragRun: "(drag to move the run dates · double-click to edit)",
      editOnly: "(double-click to edit)",
    },
    notOnPeriod: "{name} · not on this {period}",
    footer: {
      day: "Drag a bar to shift its daily time · double-click to edit.",
      other: "Drag to move run dates · drag the ends to stretch · double-click to edit.",
    },
  },

  // ── schedule-wizard.tsx ───────────────────────────────────────────────────
  wizard: {
    entryTitle: {
      play: "Play slot",
      exception_play: "Exception slot",
      off: "Off slot",
    },
    toast: {
      updateFailed: "Couldn't save the changes",
      createFailed: "Couldn't create the schedule",
      updated: "Schedule updated",
      created: "Schedule created",
    },
    conflicts: {
      title: "Saved with warnings",
      intro:
        "The schedule was saved, but these checks flagged things that may not play the way you expect:",
      acknowledge: "Got it",
      kind: {
        spots_without_base: "Spots over silence",
        entry_never_plays: "Slot never plays",
        hard_never_preempts: "Interrupt never wins",
        ambiguous_base: "Overlapping continuous slots",
        unknown: "Warning",
      },
    },
    editTitle: "Edit schedule",
    newTitle: "New schedule",
    namePrompt: "Give this schedule a name you'll recognise on the planner.",
    namePlaceholder: "e.g. Spring sale",
    notesOptional: "Notes (optional)",
    notesPlaceholder: "Internal notes",
    startFromTemplate: "Start from a template",
    templateHelp:
      "Pick a template and a playlist to jump straight to review, or skip and build it step by step.",
    wherePrompt:
      "Which devices this reaches. Pick one or more audiences, or leave on \"All devices\".",
    createSchedule: "Create schedule",
    contentFallback: "Content",
  },

  // ── time-pattern-builder.tsx ──────────────────────────────────────────────
  timePattern: {
    top: {
      all_day: "All day",
      continuous: "Continuous window",
      intervals: "At intervals",
      specifics: "Specific times",
    },
    interval: {
      range_interval: "Every N minutes (in a window)",
      range_items: "Every N songs / items",
      range_hourly: "At set minutes each hour (in a window)",
    },
    addExactTimes: "Add the exact clock times this plays.",
    removeTime: "Remove time",
    addTime: "Add time",
    every: "Every",
    minutes: "minutes",
    songsItems: "songs / items",
    minutesPastHour: "Minutes past each hour (comma-separated, e.g. 0,30)",
  },

  // ── when-builder.tsx ──────────────────────────────────────────────────────
  whenBuilder: {
    addTiming: "Add another timing (OR)",
    orPrefix: "OR · ",
    timingN: "Timing {n}",
    removeTiming: "Remove timing",
    publishWindow: "Publish window",
    always: "Always",
    period: "Period",
    dayPattern: "Day pattern",
    timePattern: "Time pattern",
    holidaysExcluded: {
      one: "{count} holiday day excluded",
      other: "{count} holiday days excluded",
    },
    holidaysAdded: {
      one: "{count} holiday day added",
      other: "{count} holiday days added",
    },
    holidaysTitle: "Holidays & special days",
    selectedShould: "Selected holidays should",
    beSkipped: "be skipped",
    beAddedIn: "be added in",
    applyPrefix: "Apply",
    holidayNoun: { one: "holiday", other: "holidays" },
    publicHolidays: "Public holidays (automatic)",
    publicHolidaysHint:
      "Uses the venue location's country. Off leaves the days unchanged.",
    holidayModeOff: "No special handling",
    holidayModeExclude: "Skip public holidays",
    holidayModeOnly: "Only on public holidays",
  },

  // ── pages/schedules.tsx ───────────────────────────────────────────────────
  // Schedule status labels: the list badge and the status filter (options and
  // trigger) all read from here, so they always match.
  status: {
    draft: "Draft",
    scheduled: "Scheduled",
    active: "Active",
    paused: "Paused",
    ended: "Ended",
  },

  page: {
    noEntries: "No slots yet",
    playCount: "{count} play",
    exceptionCount: "{count} exception",
    offCount: "{count} off",
    toast: {
      activated: "Schedule activated",
      activateFailed: "Couldn't activate the schedule",
      paused: "Schedule paused",
      pauseFailed: "Couldn't pause the schedule",
      deleted: "Schedule deleted",
      deleteFailed: "Couldn't delete the schedule",
      rescheduleFailed: "Couldn't reschedule",
    },
    confirmDelete: "Delete schedule \"{name}\"?",
    title: "Scheduling",
    description:
      "Plan what plays where and when. Music, ads, announcements, and video on one timeline.",
    quickSchedule: "Quick schedule",
    newSchedule: "New schedule",
    searchPlaceholder: "Search schedules…",
    allChannels: "All channels",
    allStatuses: "All statuses",
    audiencePlaceholder: "Audience",
    allAudiences: "All audiences",
    allDevicesNoAudience: "All devices (no audience)",
    groupByPlaceholder: "Group by",
    groupChannel: "Group: Channel",
    groupAudience: "Group: Audience",
    periodPlaceholder: "Period",
    period: { day: "Day", week: "Week", month: "Month", year: "Year" },
    errorTitle: "Couldn't load schedules",
    errorDescription:
      "Something went wrong while loading your schedules. Check your connection and try again.",
    emptyState: "Nothing scheduled yet. Create your first schedule to start planning.",
    noMatch: "No schedules match your filters.",
    emptyTitle: "Nothing scheduled yet",
    noMatchTitle: "No matches",
    clearFilters: "Clear filters",
    pause: "Pause",
    activate: "Activate",
    // Primary action on an ended schedule whose dates are all in the past:
    // pausing or activating it would change nothing, new dates would.
    editDates: "Edit dates",
    scheduleActions: "Schedule actions",
  },
} as const;
